// Rich-text editor actions. Pure, text-buffer operations the
// composer wires to the toolbar buttons and keyboard shortcuts so
// the `<TextInput>`-backed RichTextEditor behaves like a WYSIWYG
// composer over an mrkdwn-ish buffer.
//
// Behavior spec:
//   - Inline mrkdwn delimiters: `*` (bold), `_` (italic), `~`
//     (strike), `` ` `` (inline code). Single-char delimiters per
//     the mrkdwn dialect — NOT CommonMark double-star.
//   - Block prefixes: `> ` (quote), `- ` (bulleted list; we accept
//     `* ` as an alias on parse but emit `-` on toggle), `1. `
//     (numbered), ` ``` ` fenced (code block).
//   - Toggle semantics: pressing a shortcut a second time on a
//     uniformly-marked selection removes the mark. Partial-state
//     selections promote to fully-on (matches Google Docs).
//   - Code-block context inverts Enter: inside a `` ``` ``-fence,
//     plain Enter inserts a newline, Shift+Enter sends. Outside,
//     the standard `pressEnterToSend` rule applies.
//   - Backspace at column 0 of a block-prefixed line demotes the
//     block back to a plain paragraph (preserves text content).
//   - Tab / Shift+Tab inside a list item indents / outdents by two
//     spaces. We don't model true nested lists — the wire format
//     doesn't either past one level of leading whitespace.
//
// All functions are pure: `(text, selStart, selEnd) → { text,
// selStart, selEnd }`. The host applies the returned tuple
// back into state. Nothing here touches the DOM, the textarea
// ref, or React.

import type { RichTextFormat } from './RichTextFormat';

/**
 * Inline-mark delimiter table. Maps the cross-platform
 * `RichTextFormat` enum to its single-character mrkdwn marker.
 *
 * `code` aliases `inlineCode` — the public enum has both for
 * historical iOS-A / AND-A parity reasons, but the wire format
 * only has one inline-code marker. Treating them identically keeps
 * the toolbar's two chips functional without inventing a
 * second wire form.
 */
export const INLINE_MARK_BY_FORMAT: Readonly<
  Partial<Record<RichTextFormat, string>>
> = {
  bold: '*',
  italic: '_',
  strikethrough: '~',
  inlineCode: '`',
  code: '`',
};

/**
 * Block-prefix table. Each entry is the line-leading marker that
 * the mrkdwn parser uses to recognize the block. `quote` is the
 * only one driven directly by the toolbar today; bullet /
 * numbered list are exposed via keyboard shortcuts (Cmd+Shift+7 /
 * 8) and the Backspace-demotes-block path.
 */
const BLOCK_PREFIX_QUOTE = '> ';
const BLOCK_PREFIX_BULLET = '- ';
// Numbered-list lines have variable digit width; we accept any
// integer + dot + space pattern via this regex.
const BLOCK_PREFIX_NUMBERED_RE = /^(\d+)\.\s/;
const ALL_BLOCK_LINE_PREFIXES: ReadonlyArray<string | RegExp> = [
  BLOCK_PREFIX_QUOTE,
  BLOCK_PREFIX_BULLET,
  '* ',
  BLOCK_PREFIX_NUMBERED_RE,
];

const CODE_FENCE = '```';

export interface EditorRange {
  readonly text: string;
  readonly selStart: number;
  readonly selEnd: number;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Walk back from `pos` to the start of its line (column 0). */
export function findLineStart(text: string, pos: number): number {
  let i = pos;
  while (i > 0 && text[i - 1] !== '\n') i--;
  return i;
}

/** Walk forward from `pos` to the end of its line (just before `\n` or EOF). */
export function findLineEnd(text: string, pos: number): number {
  let i = pos;
  while (i < text.length && text[i] !== '\n') i++;
  return i;
}

/**
 * Count line-start `` ``` ``-fence markers strictly before `pos`.
 * Inside a code block iff the count is odd. We require the fence to
 * sit at the start of a line (`^` or after `\n`) so a stray triple
 * backtick mid-sentence doesn't flip the state.
 */
export function isInsideCodeBlock(text: string, pos: number): boolean {
  const before = text.slice(0, pos);
  let count = 0;
  let i = 0;
  while (i < before.length) {
    // At column 0 of a line?
    if (i === 0 || before[i - 1] === '\n') {
      if (before.slice(i, i + 3) === CODE_FENCE) {
        count += 1;
        i += 3;
        continue;
      }
    }
    i += 1;
  }
  return count % 2 === 1;
}

// ---------------------------------------------------------------------------
// Inline marks
// ---------------------------------------------------------------------------

/**
 * True iff the selection is immediately wrapped by `marker` on both
 * sides. `selStart === selEnd` (caret) counts as wrapped iff the
 * caret sits between two adjacent markers (e.g., caret in `*|*`).
 */
export function isInlineMarkActive(
  text: string,
  selStart: number,
  selEnd: number,
  marker: string,
): boolean {
  const left = text.slice(selStart - marker.length, selStart);
  const right = text.slice(selEnd, selEnd + marker.length);
  return left === marker && right === marker;
}

/**
 * Toggle an inline mark across `[selStart, selEnd]`. When the
 * selection is already wrapped by the marker, strip the markers and
 * shift the selection back by `marker.length`. When it isn't,
 * insert the marker on both sides and place the selection on the
 * inner content (so the next typed character lands between the
 * markers — sticky-format behavior).
 */
export function toggleInlineMark(
  text: string,
  selStart: number,
  selEnd: number,
  marker: string,
): EditorRange {
  const len = marker.length;
  if (isInlineMarkActive(text, selStart, selEnd, marker)) {
    const before = text.slice(0, selStart - len);
    const middle = text.slice(selStart, selEnd);
    const after = text.slice(selEnd + len);
    return {
      text: before + middle + after,
      selStart: selStart - len,
      selEnd: selEnd - len,
    };
  }
  const before = text.slice(0, selStart);
  const middle = text.slice(selStart, selEnd);
  const after = text.slice(selEnd);
  return {
    text: before + marker + middle + marker + after,
    selStart: selStart + len,
    selEnd: selEnd + len,
  };
}

// ---------------------------------------------------------------------------
// Line-prefix block formats (quote, bulleted list, numbered list)
// ---------------------------------------------------------------------------

function lineHasAnyBlockPrefix(line: string): boolean {
  for (const p of ALL_BLOCK_LINE_PREFIXES) {
    if (typeof p === 'string' ? line.startsWith(p) : p.test(line)) {
      return true;
    }
  }
  return false;
}

function stripAnyBlockPrefix(line: string): { stripped: string; len: number } {
  for (const p of ALL_BLOCK_LINE_PREFIXES) {
    if (typeof p === 'string') {
      if (line.startsWith(p)) return { stripped: line.slice(p.length), len: p.length };
    } else {
      const m = line.match(p);
      if (m !== null) {
        return { stripped: line.slice(m[0].length), len: m[0].length };
      }
    }
  }
  return { stripped: line, len: 0 };
}

/**
 * True iff EVERY line in the selection block starts with `prefix`.
 * For numbered lists `prefix` is a regex — `RegExp.test` gates the
 * match. Empty lines inside a block count as "not prefixed" so the
 * toolbar shows inactive when the selection straddles blocks.
 */
export function isLinePrefixActive(
  text: string,
  selStart: number,
  selEnd: number,
  prefix: string | RegExp,
): boolean {
  const start = findLineStart(text, selStart);
  const end = findLineEnd(text, selEnd);
  const lines = text.slice(start, end).split('\n');
  if (lines.length === 0) return false;
  return lines.every((line) =>
    typeof prefix === 'string' ? line.startsWith(prefix) : prefix.test(line),
  );
}

/**
 * Toggle a fixed-string line prefix (quote, bullet) across every
 * line in the selection's row block. When every line already has
 * the prefix → strip it. When at least one is missing → apply to
 * all (matches Google Docs partial-state promotion).
 *
 * Numbered lists go through {@link toggleNumberedList} which has
 * its own counter logic.
 */
export function toggleLinePrefix(
  text: string,
  selStart: number,
  selEnd: number,
  prefix: string,
): EditorRange {
  const blockStart = findLineStart(text, selStart);
  const blockEnd = findLineEnd(text, selEnd);
  const block = text.slice(blockStart, blockEnd);
  const lines = block.split('\n');
  const allHave = lines.every((line) => line.startsWith(prefix));

  let cumulativeDelta = 0;
  let firstLineDelta = 0;
  const next = lines.map((line, idx) => {
    if (allHave) {
      if (idx === 0) firstLineDelta = -prefix.length;
      cumulativeDelta -= prefix.length;
      return line.slice(prefix.length);
    }
    if (line.startsWith(prefix)) return line;
    if (idx === 0) firstLineDelta = prefix.length;
    cumulativeDelta += prefix.length;
    return prefix + line;
  });

  const newBlock = next.join('\n');
  const newText = text.slice(0, blockStart) + newBlock + text.slice(blockEnd);
  return {
    text: newText,
    selStart: selStart + firstLineDelta,
    selEnd: selEnd + cumulativeDelta,
  };
}

/**
 * Toggle a numbered list across the selection's row block. Numbers
 * restart at 1 each toggle (we don't try to detect continuation
 * across detached blocks — the wire format doesn't either past the
 * single-block scope).
 */
export function toggleNumberedList(
  text: string,
  selStart: number,
  selEnd: number,
): EditorRange {
  const blockStart = findLineStart(text, selStart);
  const blockEnd = findLineEnd(text, selEnd);
  const block = text.slice(blockStart, blockEnd);
  const lines = block.split('\n');
  const allHave = lines.every((line) => BLOCK_PREFIX_NUMBERED_RE.test(line));

  let cumulativeDelta = 0;
  let firstLineDelta = 0;
  const next = lines.map((line, idx) => {
    if (allHave) {
      const m = line.match(BLOCK_PREFIX_NUMBERED_RE);
      const len = m !== null ? m[0].length : 0;
      if (idx === 0) firstLineDelta = -len;
      cumulativeDelta -= len;
      return line.slice(len);
    }
    if (BLOCK_PREFIX_NUMBERED_RE.test(line)) return line;
    const prefix = `${idx + 1}. `;
    if (idx === 0) firstLineDelta = prefix.length;
    cumulativeDelta += prefix.length;
    return prefix + line;
  });

  const newBlock = next.join('\n');
  const newText = text.slice(0, blockStart) + newBlock + text.slice(blockEnd);
  return {
    text: newText,
    selStart: selStart + firstLineDelta,
    selEnd: selEnd + cumulativeDelta,
  };
}

// ---------------------------------------------------------------------------
// Code block (fenced)
// ---------------------------------------------------------------------------

/**
 * Toggle a fenced code block around the current row block. When the
 * caret is already inside an open `` ``` ``-fence we strip the
 * nearest fence pair; otherwise we wrap the selection's row block
 * in ``` ``` ``` ` markers. Selection lands on the inner content
 * so the user can keep typing.
 */
export function toggleCodeBlock(
  text: string,
  selStart: number,
  selEnd: number,
): EditorRange {
  if (isInsideCodeBlock(text, selStart)) {
    return stripCodeBlock(text, selStart, selEnd);
  }
  const blockStart = findLineStart(text, selStart);
  const blockEnd = findLineEnd(text, selEnd);
  const middle = text.slice(blockStart, blockEnd);
  const before = text.slice(0, blockStart);
  const after = text.slice(blockEnd);
  // Sandwich the row block between two ``` lines. The inner block
  // lands on its own row so we don't collapse content into the
  // fence row.
  const wrapped = `${CODE_FENCE}\n${middle}\n${CODE_FENCE}`;
  const openingLen = CODE_FENCE.length + 1; // ```\n
  return {
    text: before + wrapped + after,
    selStart: blockStart + openingLen + (selStart - blockStart),
    selEnd: blockStart + openingLen + (selEnd - blockStart),
  };
}

function stripCodeBlock(text: string, selStart: number, selEnd: number): EditorRange {
  // Find the opening fence line strictly before `selStart` and the
  // closing fence strictly at-or-after `selEnd`. We strip BOTH
  // fences (and the trailing newline of each) so the inner content
  // collapses back into normal paragraphs.
  const lines = text.split('\n');
  // Reconstruct cumulative offsets so we can map line→buffer index.
  const lineStarts: number[] = [0];
  for (let i = 0; i < lines.length - 1; i++) {
    const prev = lineStarts[i];
    if (prev === undefined) break;
    const line = lines[i] ?? '';
    lineStarts.push(prev + line.length + 1);
  }
  let openingLine = -1;
  let closingLine = -1;
  for (let i = 0; i < lines.length; i++) {
    if (lines[i] !== CODE_FENCE) continue;
    const lineStart = lineStarts[i] ?? 0;
    if (lineStart <= selStart) {
      openingLine = i;
    } else if (closingLine === -1 && lineStart >= selEnd) {
      closingLine = i;
      break;
    }
  }
  if (openingLine === -1 || closingLine === -1) {
    return { text, selStart, selEnd };
  }
  const openingStart = lineStarts[openingLine] ?? 0;
  const openingEnd = openingStart + CODE_FENCE.length + 1; // include trailing \n
  const closingStart = lineStarts[closingLine] ?? 0;
  // Closing fence: strip the leading \n + ``` (no trailing newline
  // — let the line after stand on its own).
  const before = text.slice(0, openingStart);
  const middle = text.slice(openingEnd, closingStart - 1); // -1 strips the \n before the fence
  const after = text.slice(closingStart + CODE_FENCE.length);
  return {
    text: before + middle + after,
    selStart: selStart - (openingEnd - openingStart),
    selEnd: selEnd - (openingEnd - openingStart),
  };
}

// ---------------------------------------------------------------------------
// Format-toggle dispatcher
// ---------------------------------------------------------------------------

/**
 * Apply a `RichTextFormat` toggle to the buffer + selection.
 *
 * The host (toolbar onPress, keyboard shortcut handler) calls this
 * with the current `text` / `selStart` / `selEnd`; the returned
 * tuple is the new state. Format → action mapping:
 *
 *   - bold / italic / strikethrough / inlineCode / code →
 *     `toggleInlineMark` with the matching mrkdwn delimiter.
 *   - quote → `toggleLinePrefix` with `> `.
 *   - codeBlock → `toggleCodeBlock` (fenced).
 *
 * Unknown formats fall through to a no-op so future enum additions
 * don't crash the composer.
 */
export function applyFormatToggle(
  format: RichTextFormat,
  text: string,
  selStart: number,
  selEnd: number,
): EditorRange {
  const marker = INLINE_MARK_BY_FORMAT[format];
  if (marker !== undefined) {
    return toggleInlineMark(text, selStart, selEnd, marker);
  }
  if (format === 'quote') {
    return toggleLinePrefix(text, selStart, selEnd, BLOCK_PREFIX_QUOTE);
  }
  if (format === 'codeBlock') {
    return toggleCodeBlock(text, selStart, selEnd);
  }
  return { text, selStart, selEnd };
}

// ---------------------------------------------------------------------------
// Active-format detection
// ---------------------------------------------------------------------------

/**
 * Derive the set of formats currently "active" at the caret /
 * selection. Drives the toolbar chip highlight + the format-shortcut
 * round-trip ("press Cmd+B again to turn off bold"). A mark is
 * active iff the entire selection (or the single caret position)
 * is wrapped by it.
 */
export function deriveActiveFormats(
  text: string,
  selStart: number,
  selEnd: number,
): ReadonlySet<RichTextFormat> {
  const active = new Set<RichTextFormat>();
  for (const [format, marker] of Object.entries(INLINE_MARK_BY_FORMAT) as Array<
    [RichTextFormat, string]
  >) {
    if (isInlineMarkActive(text, selStart, selEnd, marker)) {
      active.add(format);
    }
  }
  if (isLinePrefixActive(text, selStart, selEnd, BLOCK_PREFIX_QUOTE)) {
    active.add('quote');
  }
  if (isInsideCodeBlock(text, selStart)) {
    active.add('codeBlock');
  }
  return active;
}

// ---------------------------------------------------------------------------
// Block-aware special-key handlers
// ---------------------------------------------------------------------------

/**
 * Handle Backspace at column 0 of a block-prefixed line. Demotes
 * the line back to a plain paragraph (preserving content). Returns
 * `null` when no demotion applies; the host should let the
 * textarea handle Backspace normally.
 *
 * Only fires when there's no selection (`selStart === selEnd`) so
 * dragging-then-Backspace still deletes content like users expect.
 */
export function handleBackspaceAtBlockStart(
  text: string,
  selStart: number,
  selEnd: number,
): EditorRange | null {
  if (selStart !== selEnd) return null;
  const lineStart = findLineStart(text, selStart);
  // Caret has to be right after the prefix — i.e., at the first
  // content column of a prefixed line. We don't fire mid-line.
  const line = text.slice(lineStart, findLineEnd(text, selStart));
  if (!lineHasAnyBlockPrefix(line)) return null;
  const { len } = stripAnyBlockPrefix(line);
  if (len === 0) return null;
  // Caret must sit exactly at lineStart + len (just past the prefix)
  // for the demote-to-paragraph behavior to feel right. Otherwise
  // Backspace stays "delete one char".
  if (selStart !== lineStart + len) return null;
  const before = text.slice(0, lineStart);
  const after = text.slice(lineStart + len);
  return {
    text: before + after,
    selStart: lineStart,
    selEnd: lineStart,
  };
}

/**
 * Indent (Tab) or outdent (Shift+Tab) a list/quote line by two
 * spaces. Only fires on lines whose leading prefix is one of the
 * recognized block markers; on plain paragraphs the host should
 * fall back to default Tab behavior (move focus).
 */
export function handleTabInList(
  text: string,
  selStart: number,
  selEnd: number,
  direction: 'indent' | 'outdent',
): EditorRange | null {
  const lineStart = findLineStart(text, selStart);
  const lineEnd = findLineEnd(text, selEnd);
  const block = text.slice(lineStart, lineEnd);
  const lines = block.split('\n');
  // Need every line in the row block to be a list/quote line for
  // Tab to do anything; otherwise the user is trying to type a tab
  // mid-prose and the textarea's default focus-move is fine.
  const anyListLine = lines.some((line) => {
    const trimmed = line.replace(/^\s+/, '');
    return lineHasAnyBlockPrefix(trimmed);
  });
  if (!anyListLine) return null;

  const indent = '  ';
  let cumulativeDelta = 0;
  let firstLineDelta = 0;
  const next = lines.map((line, idx) => {
    if (direction === 'indent') {
      if (idx === 0) firstLineDelta = indent.length;
      cumulativeDelta += indent.length;
      return indent + line;
    }
    // Outdent — strip up to two leading spaces.
    let strip = 0;
    if (line.startsWith('  ')) strip = 2;
    else if (line.startsWith(' ')) strip = 1;
    if (idx === 0) firstLineDelta = -strip;
    cumulativeDelta -= strip;
    return line.slice(strip);
  });
  const newBlock = next.join('\n');
  return {
    text: text.slice(0, lineStart) + newBlock + text.slice(lineEnd),
    selStart: selStart + firstLineDelta,
    selEnd: selEnd + cumulativeDelta,
  };
}

// ---------------------------------------------------------------------------
// Keyboard shortcut → format mapping
// ---------------------------------------------------------------------------

/**
 * Pulled-out so the editor + tests share one source of truth.
 * `key` is the lowercase `event.key`; `mod` requires Cmd (mac) or
 * Ctrl (others). `shift` / `alt` are exact-match — a Cmd+B with
 * Shift pressed does NOT trigger bold.
 *
 * Bindings:
 *   - Cmd/Ctrl+B               → bold
 *   - Cmd/Ctrl+I               → italic
 *   - Cmd/Ctrl+Shift+X         → strikethrough
 *   - Cmd/Ctrl+Shift+C         → inline code
 *   - Cmd/Ctrl+Alt+Shift+C     → code block
 *   - Cmd/Ctrl+Shift+7         → numbered list
 *   - Cmd/Ctrl+Shift+8         → bulleted list
 *   - Cmd/Ctrl+Shift+9         → blockquote
 *   - Cmd/Ctrl+Shift+U         → link insertion
 */
export interface RichTextShortcut {
  readonly key: string;
  readonly shift: boolean;
  readonly alt: boolean;
  readonly action:
    | { readonly kind: 'format'; readonly format: RichTextFormat }
    | { readonly kind: 'list'; readonly listKind: 'numbered' | 'bulleted' }
    | { readonly kind: 'link' };
}

export const RICH_TEXT_SHORTCUTS: ReadonlyArray<RichTextShortcut> = [
  { key: 'b', shift: false, alt: false, action: { kind: 'format', format: 'bold' } },
  { key: 'i', shift: false, alt: false, action: { kind: 'format', format: 'italic' } },
  { key: 'x', shift: true, alt: false, action: { kind: 'format', format: 'strikethrough' } },
  { key: 'c', shift: true, alt: false, action: { kind: 'format', format: 'inlineCode' } },
  { key: 'c', shift: true, alt: true, action: { kind: 'format', format: 'codeBlock' } },
  { key: '7', shift: true, alt: false, action: { kind: 'list', listKind: 'numbered' } },
  { key: '8', shift: true, alt: false, action: { kind: 'list', listKind: 'bulleted' } },
  { key: '9', shift: true, alt: false, action: { kind: 'format', format: 'quote' } },
  { key: 'u', shift: true, alt: false, action: { kind: 'link' } },
];

/**
 * Resolve a key event (lowercased key, modifier flags) to a shortcut
 * action. Returns `null` when no shortcut matches.
 */
export function resolveShortcut(opts: {
  readonly key: string;
  readonly mod: boolean; // Cmd on mac, Ctrl elsewhere
  readonly shift: boolean;
  readonly alt: boolean;
}): RichTextShortcut['action'] | null {
  if (!opts.mod) return null;
  const key = opts.key.toLowerCase();
  for (const s of RICH_TEXT_SHORTCUTS) {
    if (s.key === key && s.shift === opts.shift && s.alt === opts.alt) {
      return s.action;
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// Link insertion
// ---------------------------------------------------------------------------

/**
 * Insert (or wrap) a link template. With a non-empty selection we
 * use the selected text as the label and place the caret inside
 * the `(url)` so the user can paste/type the URL immediately. With
 * no selection we insert a `[label](url)` template and place the
 * caret on the `label` token. We emit the user-readable
 * `[label](url)` shape; the mrkdwn renderer accepts it and round-
 * trips to the `<url|label>` wire form.
 */
export function insertLink(text: string, selStart: number, selEnd: number): EditorRange {
  const before = text.slice(0, selStart);
  const middle = text.slice(selStart, selEnd);
  const after = text.slice(selEnd);
  if (middle.length > 0) {
    const inserted = `[${middle}](url)`;
    return {
      text: before + inserted + after,
      // Caret lands on the `url` placeholder so the user can type
      // straight into it.
      selStart: selStart + middle.length + 3, // [middle](
      selEnd: selStart + middle.length + 6, // [middle](url
    };
  }
  const inserted = `[label](url)`;
  return {
    text: before + inserted + after,
    selStart: selStart + 1, // inside `[`
    selEnd: selStart + 6, // covers `label`
  };
}
